<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <div
    class="draggable-resizable-window"
    :class="windowClass"
    :style="windowStyle"
    @mousedown="handleMouseDown"
  >
    <div
      v-for="direction in ['n', 'e', 's', 'w', 'ne', 'nw', 'se', 'sw']"
      :key="direction"
      class="resize-handle"
      :class="`resize-handle-${direction} ${!enableResize ? 'disabled' : ''}`"
      @mousedown="(e) => handleResizeStart(e, direction)"
    />
    <slot />
  </div>
</template>

<script>
export default {
  name: 'DraggableResizableWindow',
  props: {
    bounds: {
      type: Object,
      default: () => ({
        minWidth: 300,
        minHeight: 200,
        top: 0,
        bottom: null,
        left: 0,
        right: null
      })
    },
    enableResize: {
      type: Boolean,
      default: true
    },
    enableDrag: {
      type: Boolean,
      default: true
    },
    windowClass: {
      type: String,
      default: ''
    },
    windowStyle: {
      type: String,
      default: 'position: fixed;'
    },
    dragHandle: {
      type: String,
      default: null // CSS selector for drag handle, if null entire window is draggable
    },
    storageKey: {
      type: String,
      default: 'draggable-resizable-window'
    },
    allowDoubleClickFullScreen: {
      type: Boolean,
      default: false
    },
    doubleClickFullScreenHandle: {
      type: String,
      default: null // CSS selector for double click handle
    }
  },
  emits: ['position-changed', 'size-changed'],
  data() {
    return {
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      isResizing: false,
      resizeDirection: null,
      saveTimeout: null,
      lastEmitTime: 0,
      fullScreen: false
    }
  },
  mounted() {
    this.restoreWindowState()

    if (this.allowDoubleClickFullScreen) {
      const el = document.querySelector(this.doubleClickFullScreenHandle)
      if (!el) return
      el.addEventListener('dblclick', this.handleDoubleClick)
    }
  },
  unmounted() {
    if (this.allowDoubleClickFullScreen) {
      const el = document.querySelector(this.doubleClickFullScreenHandle)
      if (!el) return
      el.removeEventListener('dblclick', this.handleDoubleClick)
    }
  },
  methods: {
    handleMouseDown(event) {
      if (!this.enableDrag) return

      document.querySelector(this.dragHandle).style.cursor = 'move'
      // If dragHandle is specified, only allow dragging from that element
      if (this.dragHandle) {
        if (!event.target.closest(this.dragHandle)) {
          return
        }
      }
      
      this.isDragging = true
      const rect = this.$el.getBoundingClientRect()
      this.dragOffset = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      }
      document.addEventListener('mousemove', this.handleMouseMove)
      document.addEventListener('mouseup', this.handleMouseUp)
    },
    handleMouseMove(event) {
      if (this.isDragging) {
        const x = event.clientX - this.dragOffset.x
        const y = event.clientY - this.dragOffset.y
        
        // Calculate bounds for window position
        const minLeft = this.bounds.left ?? 0
        const minTop = this.bounds.top ?? 0
        const maxLeft = this.bounds.right !== null 
          ? this.bounds.right - this.$el.offsetWidth 
          : window.innerWidth - this.$el.offsetWidth
        const maxTop = this.bounds.bottom !== null 
          ? this.bounds.bottom - this.$el.offsetHeight 
          : window.innerHeight - this.$el.offsetHeight
        
        const left = Math.min(Math.max(minLeft, x), maxLeft)
        const top = Math.min(Math.max(minTop, y), maxTop)
        
        // Update position immediately for smooth movement
        this.$el.style.left = `${left}px`
        this.$el.style.top = `${top}px`
        
        const position = { left: `${left}px`, top: `${top}px` }
        
        // Debounce localStorage writes to avoid blocking the main thread
        if (this.saveTimeout) {
          clearTimeout(this.saveTimeout)
        }
        this.saveTimeout = setTimeout(() => {
          if (this.fullScreen) return
          localStorage.setItem(`${this.storageKey}-position`, JSON.stringify(position))
        }, 100)
        
        // Throttle event emissions to reduce overhead
        const now = Date.now()
        if (now - this.lastEmitTime > 16) { // ~60fps
          this.$emit('position-changed', position)
          this.lastEmitTime = now
        }
      }
    },
    handleMouseUp() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.handleMouseMove)
      document.removeEventListener('mouseup', this.handleMouseUp)
      
      // Save final position immediately
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout)
        const left = this.$el.style.left
        const top = this.$el.style.top
        const position = { left, top }
        if (this.fullScreen) return
        localStorage.setItem(`${this.storageKey}-position`, JSON.stringify(position))
        this.$emit('position-changed', position)
      }
    },
    handleResizeStart(event, direction) {
      if (!this.enableResize) return
      event.stopPropagation()
      this.isResizing = true
      this.resizeDirection = direction
      const rect = this.$el.getBoundingClientRect()
      document.body.style.userSelect = 'none'
      
      this.dragOffset = {
        x: event.clientX,
        y: event.clientY,
        width: rect.width,
        height: rect.height,
        left: rect.left,
        top: rect.top
      }

      document.addEventListener('mousemove', this.handleResizeMove)
      document.addEventListener('mouseup', this.handleResizeEnd)
    },
    handleResizeMove(event) {
      if (!this.isResizing) return
      
      const deltaX = event.clientX - this.dragOffset.x
      const deltaY = event.clientY - this.dragOffset.y
      
      if (this.resizeDirection.includes('e')) {
        const newWidth = Math.max(this.bounds.minWidth ?? 300, this.dragOffset.width + deltaX)
        this.$el.style.width = `${newWidth}px`
      }
      
      if (this.resizeDirection.includes('w')) {
        const newWidth = Math.max(this.bounds.minWidth ?? 300, this.dragOffset.width - deltaX)
        const minLeft = this.bounds.left ?? 0
        const newLeft = Math.max(minLeft, this.dragOffset.left + (this.dragOffset.width - newWidth))
        this.$el.style.width = `${newWidth}px`
        this.$el.style.left = `${newLeft}px`
      }
      
      if (this.resizeDirection.includes('s')) {
        const newHeight = Math.max(this.bounds.minHeight ?? 200, this.dragOffset.height + deltaY)
        this.$el.style.height = `${newHeight}px`
      }
      
      if (this.resizeDirection.includes('n')) {
        const newHeight = Math.max(this.bounds.minHeight ?? 200, this.dragOffset.height - deltaY)
        const minTop = this.bounds.top ?? 0
        const newTop = Math.max(minTop, this.dragOffset.top + (this.dragOffset.height - newHeight))
        this.$el.style.height = `${newHeight}px`
        this.$el.style.top = `${newTop}px`
      }

      const size = { 
        width: `${this.$el.offsetWidth}px`, 
        height: `${this.$el.offsetHeight}px` 
      }
      
      // Debounce localStorage writes during resize
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout)
      }
      this.saveTimeout = setTimeout(() => {
        if (this.fullScreen) return
        localStorage.setItem(`${this.storageKey}-size`, JSON.stringify(size))
      }, 100)
      
      // Throttle event emissions during resize
      const now = Date.now()
      if (now - this.lastEmitTime > 16) { // ~60fps
        this.$emit('size-changed', size)
        this.lastEmitTime = now
      }
    },
    handleResizeEnd() {
      this.isResizing = false
      this.resizeDirection = null
      document.body.style.userSelect = 'auto'
      document.removeEventListener('mousemove', this.handleResizeMove)
      document.removeEventListener('mouseup', this.handleResizeEnd)
      
      // Save final size immediately
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout)
        const size = { 
          width: `${this.$el.offsetWidth}px`, 
          height: `${this.$el.offsetHeight}px` 
        }
        if (this.fullScreen) return
        localStorage.setItem(`${this.storageKey}-size`, JSON.stringify(size))
        this.$emit('size-changed', size)
      }
    },
    restoreWindowState() {
      this.$nextTick(() => {
        if (this.fullScreen) return
        const savedPosition = localStorage.getItem(`${this.storageKey}-position`)
        const savedSize = localStorage.getItem(`${this.storageKey}-size`)
        
        if (savedSize) {
          const size = JSON.parse(savedSize)
          this.$el.style.width = size.width
          this.$el.style.height = size.height
        }
        
        if (savedPosition) {
          const position = JSON.parse(savedPosition)
          this.$el.style.left = position.left
          this.$el.style.top = position.top
          this.$el.style.bottom = 'auto'
          this.$el.style.right = 'auto'
        }
      })
    },
    handleDoubleClick() {
      this.fullScreen = !this.fullScreen
      if (this.fullScreen) {
        this.$el.style.top = this.bounds.top ? `${this.bounds.top}px` : '0px'
        this.$el.style.left = this.bounds.left ? `${this.bounds.left}px` : '0px'
        this.$el.style.right = this.bounds.right ? `${this.bounds.right}px` : '0px'
        this.$el.style.bottom = this.bounds.bottom ? `${this.bounds.bottom}px` : '0px'
        this.$el.style.width = 'auto'
        this.$el.style.height = 'auto'
      } else {
        const savedPosition = JSON.parse(localStorage.getItem(`${this.storageKey}-position`)) ?? {}
        const savedSize = JSON.parse(localStorage.getItem(`${this.storageKey}-size`))  ?? {}
        this.$el.style.top = savedPosition.top ? savedPosition.top :  `${this.bounds.top}px`
        this.$el.style.left = savedPosition.left ? savedPosition.left : `${this.bounds.left}px`
        this.$el.style.width = savedSize.width ? savedSize.width : `${this.bounds.minWidth}px`
        this.$el.style.height = savedSize.height ? savedSize.height : `${this.bounds.minHeight}px`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.draggable-resizable-window {
  position: fixed;

  .resize-handle {
    position: absolute;
    z-index: 10;
    background: transparent;

    &-n:not(.disabled) {
      top: 0;
      left: 0;
      width: 100%;
      height: 8px;
      cursor: n-resize;
    }

    &-e:not(.disabled) {
      top: 0;
      right: 0;
      width: 8px;
      height: 100%;
      cursor: e-resize;
    }

    &-s:not(.disabled) {
      bottom: 0;
      left: 0;
      width: 100%;
      height: 8px;
      cursor: s-resize;
    }

    &-w:not(.disabled) {
      top: 0;
      left: 0;
      width: 8px;
      height: 100%;
      cursor: w-resize;
    }

    &-ne:not(.disabled) {
      top: 0;
      right: 0;
      width: 12px;
      height: 12px;
      cursor: ne-resize;
    }

    &-nw:not(.disabled) {
      top: 0;
      left: 0;
      width: 12px;
      height: 12px;
      cursor: nw-resize;
    }

    &-se:not(.disabled) {
      right: 0;
      bottom: 0;
      width: 12px;
      height: 12px;
      cursor: se-resize;
    }

    &-sw:not(.disabled) {
      bottom: 0;
      left: 0;
      width: 12px;
      height: 12px;
      cursor: sw-resize;
    }
  }
}
</style> 